import { useEffect, useState } from "react";
import { ChatInterface } from "../components/chat/ChatInterface";
import { MiniAppModal } from "../components/miniapps/MiniAppModal";
import { SharedWidget, WidgetConfig } from "../components/widget/SharedWidget";
import { useChatbot } from "../contexts/ChatbotContext";
import { useCustomTexts } from "../hooks/useCustomTexts";
import { useDesignColors } from "../hooks/useDesignColors";
import { useLogos } from "../hooks/useLogos";
import { useMiniApps } from "../hooks/useMiniApps";
import { useMultilingualSettings } from "../hooks/useMultilingualSettings";
import { useQuickActions } from "../hooks/useQuickActions";
import { useThemeSettings } from "../hooks/useThemeSettings";
import { ScriptSettings, useWidgetSettings } from "../hooks/useWidgetSettings";
import { supabase } from "../lib/supabase";

// Composant spécifique qui n'inclut pas de header ni de bannière de cookies
function WidgetLayout({ children }: { children: React.ReactNode }) {
  return (
    <div
      id="widget-root"
      className="min-h-screen"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: 'none'
      }}
    >
      {children}
    </div>
  );
}

export default function Widget() {
  // Vérifier si c'est un iframe embedded
  const isInIframe = window.self !== window.top;
  
  const { chatbotPublicId } = useChatbot();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { initializeColors } = useDesignColors();
  const { initializeTexts } = useCustomTexts();
  const { initializeQuickActions } = useQuickActions();
  const { initializeMiniApps } = useMiniApps();
  const { initializeLogos } = useLogos();
  const { theme, initializeTheme } = useThemeSettings();
  const { initializeSettings: initializeMultilingualSettings } =
    useMultilingualSettings();
  const {
    settings: widgetSettings,
    initializeSettings: initializeWidgetSettings,
  } = useWidgetSettings();

  // URL params for theme/lang configuration
  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get("theme");
  const lang = params.get("lang") || "fr";
  const isMobile = params.get("mobile") === "true";
  const isFloating = params.get("floating") === "true";
  const position = params.get("position") || "bottom-right";
  const offsetX = params.get("offsetX")
    ? parseInt(params.get("offsetX") || "20", 10)
    : 20;
  const offsetY = params.get("offsetY")
    ? parseInt(params.get("offsetY") || "20", 10)
    : 20;
  const showWelcomeMessage = params.get("showWelcomeMessage") === "true" || params.get("showWelcomeMessage") === null;
  const showNotificationBubble = params.get("showNotificationBubble") === "true" || params.get("showNotificationBubble") === null;
  const welcomeMessageParam = params.get("welcomeMessage");
  const customIconParam = params.get("customIcon");
  const enableAnimation = params.get("enableAnimation") !== "false";

  // Empêcher l'affichage de la bannière de cookies dans les widgets
  useEffect(() => {
    // Ajouter un style global pour masquer la bannière de cookies
    const style = document.createElement('style');
    style.innerHTML = `
      [data-testid="cookie-consent"] { display: none !important; }
      header { display: none !important; }
      .cookie-consent { display: none !important; }
      .cookies-banner { display: none !important; }
      #cookie-banner { display: none !important; }
      [id*="cookie"] { display: none !important; }
      [class*="cookie"] { display: none !important; }
      [id*="gdpr"] { display: none !important; }
      [class*="gdpr"] { display: none !important; }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Exposer les hooks useMiniAppModal et useMiniAppStore pour permettre
  // l'ouverture des mini-apps depuis le widget parent
  useEffect(() => {
    if (isInIframe && typeof window !== 'undefined') {
      // Informer le parent que le widget est prêt à recevoir des messages
      window.parent.postMessage({ type: 'widget-ready' }, '*');
    }
  }, [isInIframe]);

  const getThemeBackground = () => {
    if (themeParam) {
      switch (themeParam) {
        case "dark":
          return "bg-black";
        case "light":
          return "bg-white";
        default:
          return "bg-transparent";
      }
    }
    return "bg-transparent";
  };

  // Get script settings from URL
  const getScriptSettingsFromUrl = (): Partial<ScriptSettings> => {
    return {
      position: position as
        | "bottom-right"
        | "bottom-left"
        | "top-right"
        | "top-left",
      lang: lang as "fr" | "en" | "pt",
      offset: { x: offsetX, y: offsetY },
    };
  };

  useEffect(() => {
    const initializeWidget = async () => {
      if (!chatbotPublicId) {
        setError("ID du chatbot manquant");
        setLoading(false);
        return;
      }

      try {
        // Initialize all configurations in parallel
        await Promise.all([
          initializeColors(chatbotPublicId),
          initializeTexts(chatbotPublicId),
          initializeQuickActions(chatbotPublicId),
          initializeMiniApps(chatbotPublicId),
          initializeTheme(chatbotPublicId),
          initializeMultilingualSettings(chatbotPublicId),
          initializeWidgetSettings(chatbotPublicId),
        ]).catch((err) => {
          console.warn("Error initializing some configurations:", err);
        });

        // Get config for logos
        const { data: config } = await supabase
          .from("chatbot_configs")
          .select("logos")
          .eq("chatbot_public_id", chatbotPublicId)
          .single();

        if (config?.logos && typeof config.logos === "object") {
          initializeLogos(
            config.logos as {
              chatbotLogo: string | null;
              botIcon: string | null;
            }
          );
        }

        setLoading(false);
      } catch (err) {
        console.error("Error initializing widget:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Une erreur est survenue lors de l'initialisation"
        );
        setLoading(false);
      }
    };

    initializeWidget();
  }, [chatbotPublicId]);

  // Apply theme and language to document
  useEffect(() => {
    if (themeParam === "dark" || theme.mode === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    if (lang) {
      document.documentElement.lang = lang;
    }

    // Set page title and favicon
    document.title = "Botmaker Agency";
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.setAttribute("href", "https://i.imgur.com/SBg632A.png");
    }
  }, [themeParam, theme.mode, lang]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
        </div>
      );
    }

    if (error || !chatbotPublicId) {
      return (
        <div className="flex items-center justify-center h-screen text-red-400 p-4 text-center">
          <p>{error || "Configuration non trouvée"}</p>
        </div>
      );
    }

    if (isFloating && widgetSettings) {
      // Create widget config from params and received config
      const scriptSettings = getScriptSettingsFromUrl();

      const widgetConfig: WidgetConfig = {
        mode: "floating",
        position: scriptSettings.position,
        offset: scriptSettings.offset,
        lang: scriptSettings.lang,
        // Prioritize URL params over database settings
        customIcon: customIconParam || widgetSettings.customIcon || undefined,
        welcomeMessage: welcomeMessageParam || widgetSettings.welcomeMessage,
        showWelcomeMessage: showWelcomeMessage,
        showNotificationBubble: showNotificationBubble,
        enableAnimation: enableAnimation,
      };

      return <SharedWidget 
        config={widgetConfig} 
        publicId={chatbotPublicId} 
        preview={false} 
        isMobile={isMobile} 
      />;
    }

    return (
      <div
        className={`h-dvh flex flex-col ${getThemeBackground()}`}
        style={{
          pointerEvents: "auto",
        }}
      >
        <div className={`flex-1 w-full h-full ${isMobile ? "p-0" : "md:p-4"}`}>
          <div
            className={`w-full h-full ${
              isMobile ? "rounded-none" : "rounded-none md:rounded-xl"
            }`}
          >
            <ChatInterface preview={false} isWidget={true} publicId={chatbotPublicId} />
          </div>
        </div>
      </div>
    );
  };

  // On utilise le WidgetLayout qui n'a pas de header ni de bannière de cookies
  return (
    <WidgetLayout>
      {renderContent()}
      <MiniAppModal />
    </WidgetLayout>
  );
}